import { useState, type FormEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

export default function LoginPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/connections'

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!username.trim() || !password.trim()) return
    setLoading(true)
    setError(null)

    try {
      const resp = await fetch('/auth-api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: username.trim(), password }),
      })

      if (!resp.ok) {
        const body = await resp.text()
        let msg = 'Login failed'
        try { const j = JSON.parse(body); msg = j?.message || j?.error || msg } catch { /* not JSON */ }
        setError(msg)
        return
      }

      const data = await resp.json()
      const token = data.token || data.accessToken
      if (!token) {
        setError('Login succeeded but no token returned')
        return
      }

      localStorage.setItem('smm_token', token)

      try {
        const initResp = await fetch('/api/v1/auth/sandbox-init', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        })
        if (initResp.ok) {
          const initData = await initResp.json()
          if (initData.token) {
            localStorage.setItem('smm_token', initData.token)
          }
        }
      } catch (initErr) {
        console.error('[Sandbox] workspace init failed (non-blocking)', initErr)
      }

      window.dispatchEvent(new Event('storage'))
      navigate(from, { replace: true })
    } catch (err) {
      console.error('[Sandbox] login error', err)
      setError('Cannot reach the server -- check your connection')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 420,
        padding: '2.5rem 2rem',
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img
            src="/images/entertainment-banner.png"
            alt="entertainment.fm"
            style={{ maxWidth: 280, height: 'auto', marginBottom: '1rem' }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
          <h1 style={{
            fontSize: '1.4rem',
            fontWeight: 700,
            color: '#1a1a2e',
            margin: 0,
            letterSpacing: '-0.02em',
          }}>
            Social.entertainment.FM
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div style={{
              padding: '0.75rem 1rem',
              background: '#ffebee',
              color: '#c62828',
              borderRadius: 6,
              fontSize: '0.85rem',
              marginBottom: '1rem',
            }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#555', marginBottom: 4 }}>
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoFocus
              autoComplete="username"
              style={{
                width: '100%',
                padding: '0.7rem 0.9rem',
                border: '1.5px solid #ddd',
                borderRadius: 8,
                fontSize: '0.95rem',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box',
              }}
              onFocus={e => e.target.style.borderColor = '#4361ee'}
              onBlur={e => e.target.style.borderColor = '#ddd'}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#555', marginBottom: 4 }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                style={{
                  width: '100%',
                  padding: '0.7rem 2.8rem 0.7rem 0.9rem',
                  border: '1.5px solid #ddd',
                  borderRadius: 8,
                  fontSize: '0.95rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box',
                }}
                onFocus={e => e.target.style.borderColor = '#4361ee'}
                onBlur={e => e.target.style.borderColor = '#ddd'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
                style={{
                  position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888',
                }}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !username.trim() || !password.trim()}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: loading ? '#999' : '#4361ee',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: '1rem',
              fontWeight: 600,
              cursor: loading ? 'wait' : 'pointer',
              transition: 'background 0.2s',
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>

      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', marginTop: '2rem' }}>
        Social Media Management Sandbox
      </div>
    </div>
  )
}
